import { useEffect, useRef, useState } from "react";
import { Copy, ExternalLink, ImagePlus, Loader2, Palette, RefreshCw, Save, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import ColorField from "@/components/admin/ColorField";

// Sprint 09J — "Ticketontwerp": lets an organizer set the image that is actually
// rendered on this event's PDF tickets (backend/server.py's _render_tickets_pdf,
// the one existing renderer — see admin_upload_ticket_image/admin_use_cover_as_
// ticket_image/admin_delete_ticket_image/admin_ticket_preview_pdf). Reuses the
// exact upload-button + preview + vervangen/verwijderen convention already
// established by AdminEventCompanion.jsx's RouteMediaField/NewsImageField.
//
// Live preview (follow-up fix): the original "Bekijk voorbeeldticket" button opened
// the real preview PDF in a new tab on manual click only — it never updated itself,
// so an admin adjusting the ticket image had to re-click it after every change and
// compare two separate windows. This now embeds the exact same, real
// GET /admin/events/{id}/ticket-preview.pdf response inline (companion-app's
// RouteMedia.jsx already established the same <iframe src={pdfBlobUrl}> convention
// for in-app PDF viewing) and refetches it automatically on mount and after every
// successful image/theme change — no second, approximate CSS mockup is introduced;
// it is always the real renderer's real output.
//
// Ticket-theme editor (this round): per-event OVERRIDE of colors/font/logo/footer
// note (backend PATCH /admin/events/{id}/ticket-theme, merged over the
// organisation's own ticket_theme via _effective_ticket_theme — see AdminOrgTheme.jsx
// for the org-level base editor, unchanged) plus a second, separate full-bleed
// background image (upload-ticket-background/ticket-background/use-cover/delete —
// mirrors the hero-image endpoints exactly, own storage prefix, never confused with
// ticket_image_url). An event with no override of a given field keeps using its
// organisation's value byte-for-byte, exactly like before this feature.

const FONT_FAMILY_OPTIONS = [
  { value: "helvetica", label: "Standaard (Helvetica)" },
  { value: "times", label: "Klassiek (Times)" },
  { value: "courier", label: "Monospace (Courier)" },
];
const LOGO_POSITION_OPTIONS = [
  { value: "top-right", label: "Rechtsboven" },
  { value: "top-left", label: "Linksboven" },
];
const PLATFORM_DEFAULTS = {
  primary: "#e8630a",
  accent: "#06b6d4",
  bg: "#162235",
  text: "#ffffff",
  footer_note: "",
  font_family: "helvetica",
  logo_position: "top-right",
  logo_size_mm: 14,
};

export default function AdminEventTicketDesign({
  eventId, authAxios, ticketImageUrl, coverImageUrl, onChanged,
  ticketTheme, onThemeChanged, ticketBackgroundImageUrl, onBackgroundChanged, orgTicketTheme,
  isSuperadmin,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [usingCover, setUsingCover] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const previewUrlRef = useRef(null);

  const bgFileInputRef = useRef(null);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [usingCoverBg, setUsingCoverBg] = useState(false);
  const [removingBg, setRemovingBg] = useState(false);

  // The organisation's own effective theme is the fallback base for every field
  // this event doesn't explicitly override — mirrors backend's _org_theme merge,
  // done here client-side purely for display (what will actually render).
  const orgBase = { ...PLATFORM_DEFAULTS, ...(orgTicketTheme || {}) };
  const [themeDraft, setThemeDraft] = useState(ticketTheme || {});
  const [savingTheme, setSavingTheme] = useState(false);
  useEffect(() => {
    setThemeDraft(ticketTheme || {});
  }, [eventId, ticketTheme]);

  const displayValue = (key) => (themeDraft[key] !== undefined ? themeDraft[key] : orgBase[key]);
  const isOverridden = (key) => themeDraft[key] !== undefined && themeDraft[key] !== "";
  const setField = (key, value) => setThemeDraft((d) => ({ ...d, [key]: value }));
  const clearField = (key) => setThemeDraft((d) => {
    const next = { ...d };
    delete next[key];
    return next;
  });

  const refreshPreview = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/ticket-preview.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (err) {
      setPreviewError(err?.response?.data?.detail || err.message || "Voorbeeld kon niet worden gegenereerd");
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    refreshPreview();
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bestand te groot (max 5MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authAxios.post(`/admin/events/${eventId}/upload-ticket-image`, fd);
      toast.success("Ticketafbeelding geüpload");
      onChanged(data.ticket_image_url);
      await refreshPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const useCover = async () => {
    setUsingCover(true);
    try {
      const { data } = await authAxios.post(`/admin/events/${eventId}/ticket-image/use-cover`);
      toast.success("Eventcover gebruikt als ticketafbeelding");
      onChanged(data.ticket_image_url);
      await refreshPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUsingCover(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Ticketafbeelding verwijderen? Nieuwe PDF-tickets tonen dan geen afbeelding meer.")) return;
    setRemoving(true);
    try {
      await authAxios.delete(`/admin/events/${eventId}/ticket-image`);
      toast.success("Ticketafbeelding verwijderd");
      onChanged(null);
      await refreshPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setRemoving(false);
    }
  };

  const onBgFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bestand te groot (max 5MB)");
      return;
    }
    setUploadingBg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authAxios.post(`/admin/events/${eventId}/upload-ticket-background`, fd);
      toast.success("Achtergrondafbeelding geüpload");
      onBackgroundChanged(data.ticket_background_image_url);
      await refreshPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploadingBg(false);
      if (bgFileInputRef.current) bgFileInputRef.current.value = "";
    }
  };

  const useCoverBg = async () => {
    setUsingCoverBg(true);
    try {
      const { data } = await authAxios.post(`/admin/events/${eventId}/ticket-background/use-cover`);
      toast.success("Eventcover gebruikt als achtergrond");
      onBackgroundChanged(data.ticket_background_image_url);
      await refreshPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUsingCoverBg(false);
    }
  };

  const removeBg = async () => {
    if (!window.confirm("Achtergrondafbeelding verwijderen?")) return;
    setRemovingBg(true);
    try {
      await authAxios.delete(`/admin/events/${eventId}/ticket-background`);
      toast.success("Achtergrondafbeelding verwijderd");
      onBackgroundChanged(null);
      await refreshPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setRemovingBg(false);
    }
  };

  const saveTheme = async () => {
    setSavingTheme(true);
    try {
      const { data } = await authAxios.patch(`/admin/events/${eventId}/ticket-theme`, { ticket_theme: themeDraft });
      setThemeDraft(data.ticket_theme || {});
      onThemeChanged?.(data.ticket_theme || {});
      toast.success("Ticketstijl opgeslagen");
      await refreshPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setSavingTheme(false);
    }
  };

  const resetAllToOrgDefault = () => setThemeDraft({});

  const openPreviewInNewTab = () => {
    if (previewUrl) {
      window.open(previewUrl, "_blank");
    } else {
      refreshPreview();
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-svj-card border border-svj-border rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-svj-cyan" />
            <div className="text-sm font-semibold text-white uppercase tracking-wider">Kleuren, lettertype &amp; logo</div>
          </div>
          <button
            type="button"
            onClick={resetAllToOrgDefault}
            data-testid="ticket-theme-reset-all"
            className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"
          >
            <Undo2 className="h-3 w-3" /> Alles naar organisatie-standaard
          </button>
        </div>
        <p className="text-xs text-slate-400">
          Overschrijft alleen voor dit event de standaardstijl van je organisatie. Een veld dat je hier niet aanpast
          blijft de organisatie-instelling volgen — ook als die later wijzigt.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ColorField label="Primair (gradient/header)" value={displayValue("primary")} onChange={(v) => setField("primary", v)} testid="ticket-theme-primary" />
          <ColorField label="Accent" value={displayValue("accent")} onChange={(v) => setField("accent", v)} testid="ticket-theme-accent" />
          <ColorField label="Achtergrondkleur kaart" value={displayValue("bg")} onChange={(v) => setField("bg", v)} testid="ticket-theme-bg" />
          <ColorField label="Tekst" value={displayValue("text")} onChange={(v) => setField("text", v)} testid="ticket-theme-text" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Lettertype</span>
            <select
              value={displayValue("font_family")}
              onChange={(e) => setField("font_family", e.target.value)}
              data-testid="ticket-theme-font-family"
              className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            >
              {FONT_FAMILY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <div />
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Logo-positie</span>
            <select
              value={displayValue("logo_position")}
              onChange={(e) => setField("logo_position", e.target.value)}
              data-testid="ticket-theme-logo-position"
              className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            >
              {LOGO_POSITION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              Logogrootte ({displayValue("logo_size_mm")}mm)
            </span>
            <input
              type="range"
              min={8}
              max={30}
              value={displayValue("logo_size_mm")}
              onChange={(e) => setField("logo_size_mm", Number(e.target.value))}
              data-testid="ticket-theme-logo-size"
              className="mt-3 w-full accent-svj-cyan"
            />
          </label>
        </div>

        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Voetnoot op ticket (optioneel)</span>
          <input
            value={displayValue("footer_note") || ""}
            onChange={(e) => setField("footer_note", e.target.value)}
            placeholder="Bijv. 'Neem legitimatie mee'"
            maxLength={120}
            data-testid="ticket-theme-footer-note"
            className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
          />
        </label>

        <label className={`flex items-center gap-2 text-sm ${isSuperadmin ? "text-slate-200" : "text-slate-500"}`}>
          <input
            type="checkbox"
            checked={!displayValue("hide_platform_brand")}
            onChange={(e) => setField("hide_platform_brand", !e.target.checked)}
            disabled={!isSuperadmin}
            data-testid="ticket-theme-show-platform-brand"
            className="h-4 w-4 rounded border-svj-border bg-svj-bg accent-svj-cyan disabled:opacity-50 disabled:cursor-not-allowed"
          />
          Toon "Tickets by StudioVoorjou" rechtsonderin op ticket
          {!isSuperadmin && <span className="text-[10px] text-slate-500">(alleen superadmin)</span>}
        </label>

        {Object.keys(themeDraft).length > 0 && (
          <div className="text-[10px] text-slate-500">
            Aangepast voor dit event: {Object.keys(themeDraft).filter((k) => isOverridden(k)).join(", ") || "geen"}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={saveTheme}
            disabled={savingTheme}
            data-testid="ticket-theme-save"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold py-2 px-5 text-sm disabled:opacity-60"
          >
            {savingTheme ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Ticketstijl opslaan
          </button>
        </div>
      </div>

      <div className="bg-svj-card border border-svj-border rounded-2xl p-6 space-y-4">
        <div>
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Ticketafbeelding</div>
          <p className="mt-1 text-xs text-slate-400">
            Deze afbeelding verschijnt bovenaan op het PDF-ticket van dit event. Optioneel — zonder afbeelding ziet het
            ticket eruit zoals nu.
          </p>
        </div>

        {ticketImageUrl ? (
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <img
              src={ticketImageUrl}
              alt="Ticketafbeelding"
              className="w-full sm:w-64 aspect-[16/9] object-cover rounded-xl border border-svj-border"
            />
            <div className="flex flex-wrap gap-2">
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="ticket-image-input" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="ticket-image-replace"
                className="inline-flex items-center gap-2 rounded-full border-2 border-svj-cyan text-svj-cyan font-bold py-1.5 px-4 text-xs hover:bg-svj-cyan/10 transition-colors disabled:opacity-60"
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} Vervangen
              </button>
              <button
                type="button"
                onClick={remove}
                disabled={removing}
                data-testid="ticket-image-remove"
                className="inline-flex items-center gap-2 rounded-full border-2 border-red-500/60 text-red-400 font-bold py-1.5 px-4 text-xs hover:bg-red-500/10 transition-colors disabled:opacity-60"
              >
                {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Verwijderen
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="ticket-image-input" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="ticket-image-upload"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold py-2 px-4 text-xs disabled:opacity-60"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} Upload ticketafbeelding
            </button>
            {coverImageUrl && (
              <button
                type="button"
                onClick={useCover}
                disabled={usingCover}
                data-testid="ticket-image-use-cover"
                className="inline-flex items-center gap-2 rounded-full border border-svj-border text-slate-200 hover:text-white font-bold py-2 px-4 text-xs disabled:opacity-60"
              >
                {usingCover ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />} Gebruik eventcover
              </button>
            )}
            <span className="text-[10px] text-slate-500">JPG/PNG/WEBP · max 5MB</span>
          </div>
        )}
      </div>

      <div className="bg-svj-card border border-svj-border rounded-2xl p-6 space-y-4">
        <div>
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Achtergrondafbeelding</div>
          <p className="mt-1 text-xs text-slate-400">
            Een volledige achtergrondafbeelding achter het hele ticket (los van de ticketafbeelding hierboven, die
            alleen bovenaan de kaart staat). Optioneel.
          </p>
        </div>

        {ticketBackgroundImageUrl ? (
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <img
              src={ticketBackgroundImageUrl}
              alt="Achtergrondafbeelding"
              className="w-full sm:w-64 aspect-[3/4] object-cover rounded-xl border border-svj-border"
            />
            <div className="flex flex-wrap gap-2">
              <input ref={bgFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onBgFile} className="hidden" data-testid="ticket-background-input" />
              <button
                type="button"
                onClick={() => bgFileInputRef.current?.click()}
                disabled={uploadingBg}
                data-testid="ticket-background-replace"
                className="inline-flex items-center gap-2 rounded-full border-2 border-svj-cyan text-svj-cyan font-bold py-1.5 px-4 text-xs hover:bg-svj-cyan/10 transition-colors disabled:opacity-60"
              >
                {uploadingBg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} Vervangen
              </button>
              <button
                type="button"
                onClick={removeBg}
                disabled={removingBg}
                data-testid="ticket-background-remove"
                className="inline-flex items-center gap-2 rounded-full border-2 border-red-500/60 text-red-400 font-bold py-1.5 px-4 text-xs hover:bg-red-500/10 transition-colors disabled:opacity-60"
              >
                {removingBg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Verwijderen
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <input ref={bgFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onBgFile} className="hidden" data-testid="ticket-background-input" />
            <button
              type="button"
              onClick={() => bgFileInputRef.current?.click()}
              disabled={uploadingBg}
              data-testid="ticket-background-upload"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold py-2 px-4 text-xs disabled:opacity-60"
            >
              {uploadingBg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} Upload achtergrond
            </button>
            {coverImageUrl && (
              <button
                type="button"
                onClick={useCoverBg}
                disabled={usingCoverBg}
                data-testid="ticket-background-use-cover"
                className="inline-flex items-center gap-2 rounded-full border border-svj-border text-slate-200 hover:text-white font-bold py-2 px-4 text-xs disabled:opacity-60"
              >
                {usingCoverBg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />} Gebruik eventcover
              </button>
            )}
            <span className="text-[10px] text-slate-500">JPG/PNG/WEBP · max 5MB</span>
          </div>
        )}
      </div>

      <div className="bg-svj-card border border-svj-border rounded-2xl p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-white uppercase tracking-wider">Live voorbeeld</div>
            <p className="mt-1 text-xs text-slate-400">
              De echte PDF met de huidige eventgegevens, kleuren en (indien ingesteld) je ticketafbeelding — ververst
              automatisch na elke wijziging hierboven. Dit voorbeeld is nooit een geldig toegangsticket.
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={refreshPreview}
              disabled={previewLoading}
              data-testid="ticket-preview-refresh"
              title="Voorbeeld vernieuwen"
              className="inline-flex items-center gap-2 rounded-full border border-svj-border text-slate-300 hover:text-white font-bold py-1.5 px-3 text-xs disabled:opacity-60"
            >
              {previewLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              onClick={openPreviewInNewTab}
              disabled={previewLoading || !previewUrl}
              data-testid="ticket-preview-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-svj-cyan text-svj-cyan font-bold py-1.5 px-4 text-xs hover:bg-svj-cyan/10 transition-colors disabled:opacity-60"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Nieuw tabblad
            </button>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-svj-border bg-svj-bg/60 overflow-hidden" style={{ height: 560 }}>
          {previewError ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-center px-6">
              <span className="text-xs text-red-400">{previewError}</span>
              <button
                type="button"
                onClick={refreshPreview}
                className="text-xs text-svj-cyan hover:underline"
              >
                Opnieuw proberen
              </button>
            </div>
          ) : previewUrl ? (
            <iframe
              src={previewUrl}
              title="Voorbeeldticket"
              data-testid="ticket-preview-iframe"
              className="w-full h-full"
            />
          ) : (
            <div className="h-full flex items-center justify-center gap-2 text-slate-500 text-xs">
              <Loader2 className="h-4 w-4 animate-spin" /> Voorbeeld wordt gegenereerd…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
